/* Vidéos produit DAÏQUIRI : bulles + lecteur plein écran (remplace Vidjet).
   Le lecteur est ajouté à <body> pour échapper aux colonnes collantes de Prestige.
   Lecture : playsinline + muted (exigé par iOS pour démarrer seul), son au premier appui.
   Panier : /cart/add.js puis l'événement cart:change de Prestige (ouvre le tiroir panier).
   Mesure : événements video_story_* poussés dans window.dataLayer (balise GA4 à créer dans GTM). */
(() => {
  if (window.customElements.get('video-stories')) return;

  const track = (event, data) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event }, data));
  };

  // Sources exposées par Shopify au thème : HLS adaptatif (m3u8, 480p à 1080p) + un seul MP4, en 1080p.
  // HLS quand le navigateur le lit nativement ET qu'il s'agit d'Apple (Safari, iPhone) ou d'un mobile :
  // le débit s'adapte au réseau (Chrome démarre en 480p quelques secondes, acceptable sur téléphone).
  // Sur ordinateur hors Safari : le MP4 1080p, sans phase en basse définition.
  //
  // Flux Mux (1080×1920 à 4K), quand la vidéo en a un : lecture native sur Safari et iPhone ; ailleurs via
  // hls.js (le HLS intégré de Chrome ne lit pas les flux Mux à audio séparé), hébergé dans le thème et chargé
  // seulement à ce moment-là. En cas d'échec : le MP4 Shopify.
  // Plafond Mux selon la hauteur réellement affichée de la vidéo 9:16 : 1080p si elle tient en 1920 px
  // physiques (ordinateurs courants), 1440p au-delà (téléphones récents, très grands écrans). Jamais la 4K
  // à l'écran : elle pèse 2 à 3 fois plus pour un gain invisible.
  const probe = document.createElement('video');
  const nativeHls = !!probe.canPlayType('application/vnd.apple.mpegurl');
  const isApple = /Apple/.test(navigator.vendor || '');
  const canMse = !!(window.ManagedMediaSource || window.MediaSource);
  const preferHls = isApple || window.matchMedia('(max-width: 767px), (pointer: coarse)').matches
    || !!(navigator.connection && navigator.connection.saveData);
  const muxCap = () => {
    const w = window.innerWidth || screen.width;
    const h = window.innerHeight || screen.height;
    const contain = w / h >= 0.75; // même seuil que le CSS (.vs-video en contain à partir de 3/4)
    const shown = contain ? Math.min(h, w * 16 / 9) : Math.max(h, w * 16 / 9);
    return shown * (window.devicePixelRatio || 1) > 1920 ? '1440p' : '1080p';
  };
  const pickSource = (sources) => {
    const mux = sources.find((s) => s.mux);
    const muxUrl = mux && `${mux.url}?max_resolution=${muxCap()}`;
    if (mux && isApple && nativeHls) return { url: muxUrl, engine: 'native' };
    if (mux && canMse) return { url: muxUrl, engine: 'hls' };
    const rest = sources.filter((s) => !s.mux);
    const hls = rest.find((s) => s.format === 'm3u8');
    const mp4 = rest.filter((s) => s.format === 'mp4').sort((a, b) => b.height - a.height)[0];
    if (hls && nativeHls && (preferHls || !mp4)) return { url: hls.url, engine: 'native' };
    return { url: mp4 ? mp4.url : (hls ? hls.url : ''), engine: 'native' };
  };
  const fallbackSource = (sources, current) => {
    const mp4 = sources.filter((s) => s.format === 'mp4').sort((a, b) => b.height - a.height)[0];
    return mp4 && mp4.url !== current ? mp4.url : null;
  };
  // Débit de départ de hls.js : celui mesuré sur la vidéo précédente (plafonné, car une vidéo revue sort du
  // cache et fausse la mesure), sinon l'indication réseau du navigateur, sinon ~6 Mbit/s (départ vers 720p).
  const BW_START = 6000000;
  let lastBw = 0;
  const startBandwidth = () => {
    if (lastBw) return lastBw;
    const c = navigator.connection;
    if (c && (c.saveData || /(^|-)(2g|3g)$/.test(c.effectiveType || ''))) return 1000000;
    if (c && c.downlink) return Math.min(BW_START, c.downlink * 800000);
    return BW_START;
  };
  let hlsPromise = null;
  const loadHls = (src) => hlsPromise || (hlsPromise = new Promise((resolve, reject) => {
    if (window.Hls) { resolve(window.Hls); return; }
    if (!src) { reject(new Error('hls.js')); return; }
    const tag = document.createElement('script');
    tag.src = src;
    tag.async = true;
    tag.onload = () => (window.Hls ? resolve(window.Hls) : reject(new Error('hls.js')));
    tag.onerror = () => { hlsPromise = null; reject(new Error('hls.js')); };
    document.head.appendChild(tag);
  }));

  class VideoStories extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-video-stories-config]').textContent);
      } catch (e) {
        return;
      }
      this.bubbles = [...this.querySelectorAll('.video-stories__bubble')];
      this.stories = this.bubbles.map((b) => {
        let sources = [];
        try { sources = JSON.parse(b.dataset.sources || '[]'); } catch (e) { /* bulle sans source : ignorée au chargement */ }
        return { sources, poster: b.dataset.poster, label: b.dataset.label };
      });
      this.bubbles.forEach((b, i) => b.addEventListener('click', () => this.open(i, b)));
      // hls.js préchargé dès qu'on s'approche d'une bulle, pour ne pas retarder le démarrage.
      this.hlsSrc = this.dataset.hls;
      if (this.stories.some((s) => pickSource(s.sources).engine === 'hls')) {
        const warm = () => loadHls(this.hlsSrc).catch(() => {});
        this.bubbles.forEach((b) => ['pointerenter', 'touchstart', 'focus'].forEach((ev) => b.addEventListener(ev, warm, { once: true, passive: true })));
      }
      this.current = 0;
      // Son activé par défaut : le toucher sur la bulle autorise la lecture sonore. Si le visiteur coupe le son,
      // ce choix est gardé pour les vidéos suivantes de la page.
      this.soundOn = true;
      this.isOpen = false;
    }

    // Prestige re-rend le bloc à chaque changement de taille : on retire le lecteur de l'ancienne instance.
    disconnectedCallback() {
      if (!this.player) return;
      if (this.isOpen) this.teardown();
      window.removeEventListener('popstate', this.onPopState);
      this.player.remove();
      this.player = null;
      this._ready = false;
    }

    build() {
      const t = this.config.i18n;
      const el = document.createElement('div');
      el.className = 'vs-player';
      el.hidden = true;
      el.tabIndex = -1;
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', this.config.title);
      el.innerHTML = `
        <div class="vs-stage">
          <video class="vs-video" playsinline webkit-playsinline muted preload="none"></video>
          <button type="button" class="vs-tap vs-tap--prev" aria-label="${t.prev}"></button>
          <button type="button" class="vs-tap vs-tap--mid" tabindex="-1" aria-hidden="true"></button>
          <button type="button" class="vs-tap vs-tap--next" aria-label="${t.next}"></button>
          <button type="button" class="vs-play" hidden aria-label="${t.play}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor"/></svg></button>
          <button type="button" class="vs-sound">
            <svg class="vs-sound__on" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 4V5L7 9H3z" fill="currentColor"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>
            <svg class="vs-sound__off" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 4V5L7 9H3z" fill="currentColor"/><path d="M16 9.5l5 5M21 9.5l-5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>
          </button>
          <div class="vs-top">
            <div class="vs-progress">${this.stories.map(() => '<i><b></b></i>').join('')}</div>
            <div class="vs-toprow">
              <span class="vs-label"></span>
              <button type="button" class="vs-close" aria-label="${t.close}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" fill="none"/></svg></button>
            </div>
          </div>
          <div class="vs-toast" role="status"></div>
        </div>
        <form class="vs-card">
          ${this.config.image ? `<img class="vs-card__img" src="${this.config.image}" alt="" width="56" height="56">` : ''}
          <div class="vs-card__info">
            <span class="vs-card__title"></span>
            <span class="vs-card__price"></span>
            <label class="vs-card__size" ${this.config.single ? 'hidden' : ''}>
              <span class="vs-visually-hidden">${t.size}</span>
              <select class="vs-card__select"></select>
            </label>
          </div>
          <button type="submit" class="vs-card__add"></button>
        </form>`;
      document.body.appendChild(el);

      this.player = el;
      this.video = el.querySelector('.vs-video');
      this.segments = [...el.querySelectorAll('.vs-progress b')];
      this.labelEl = el.querySelector('.vs-label');
      this.closeBtn = el.querySelector('.vs-close');
      this.soundBtn = el.querySelector('.vs-sound');
      this.playBtn = el.querySelector('.vs-play');
      this.toast = el.querySelector('.vs-toast');
      this.select = el.querySelector('.vs-card__select');
      this.priceEl = el.querySelector('.vs-card__price');
      this.addBtn = el.querySelector('.vs-card__add');
      el.querySelector('.vs-card__title').textContent = this.config.title;

      this.config.variants.forEach((v) => {
        const o = document.createElement('option');
        o.value = v.id;
        o.textContent = v.available ? v.title : `${v.title} (${t.soldOut})`;
        o.disabled = !v.available;
        this.select.appendChild(o);
      });
      this.select.addEventListener('change', () => this.syncVariant());

      // Un double appui sur une bulle ne doit pas tomber sur les zones du lecteur qui vient de s'ouvrir.
      el.addEventListener('click', (e) => {
        if (performance.now() - (this.openedAt || 0) < 500) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      this.video.addEventListener('timeupdate', () => this.paintProgress());
      this.video.addEventListener('ended', () => {
        track('video_story_complete', this.trackData());
        this.next();
      });
      this.video.addEventListener('play', () => this.setPlayButton(false));
      // Flux HLS illisible : on repasse sur le MP4 de la même vidéo.
      this.video.addEventListener('error', () => {
        if (this.isOpen && !this.hls) this.useFallback(this.current);
      });
      this.video.addEventListener('pause', () => {
        // Ignore la pause émise par le changement de source entre deux vidéos.
        if (this.isOpen && !this.loading && !this.video.ended) this.setPlayButton(true);
      });

      el.querySelector('.vs-tap--prev').addEventListener('click', () => this.load(Math.max(0, this.current - 1)));
      el.querySelector('.vs-tap--next').addEventListener('click', () => this.next());
      el.querySelector('.vs-tap--mid').addEventListener('click', () => (this.video.paused ? this.play() : this.video.pause()));
      this.playBtn.addEventListener('click', () => this.play());
      this.closeBtn.addEventListener('click', () => this.close());
      this.soundBtn.addEventListener('click', () => {
        this.soundOn = !this.soundOn;
        this.video.muted = !this.soundOn;
        this.paintSound();
        if (this.soundOn) this.play();
      });
      el.querySelector('.vs-card').addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });

      // Clavier écouté sur le document tant que le lecteur est ouvert (le focus peut retomber sur <body>).
      this.onKey = (e) => {
        if (!this.isOpen) return;
        const inField = e.target instanceof Element && e.target.closest('select, input, textarea');
        if (e.key === 'Escape') { e.preventDefault(); this.close(); }
        else if (e.key === 'ArrowRight' && !inField) this.next();
        else if (e.key === 'ArrowLeft' && !inField) this.load(Math.max(0, this.current - 1));
        else if (e.key === 'Tab') this.trapFocus(e);
      };

      let y0 = null;
      const stage = el.querySelector('.vs-stage');
      stage.addEventListener('touchstart', (e) => { y0 = e.touches[0].clientY; }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        if (y0 !== null && e.changedTouches[0].clientY - y0 > 90) this.close();
        y0 = null;
      });

      this.onPopState = () => { if (this.isOpen) this.teardown(); };
      window.addEventListener('popstate', this.onPopState);
    }

    trackData() {
      return { video_label: this.stories[this.current].label, video_index: this.current + 1, product_id: this.config.productId };
    }

    open(index, trigger) {
      if (this.isOpen) return;
      if (!this.player) this.build();
      this.trigger = trigger;
      this.isOpen = true;
      this.adding = false;
      this.openedAt = performance.now();
      this.player.hidden = false;
      document.documentElement.classList.add('vs-lock');
      document.addEventListener('keydown', this.onKey);
      this.paintSound();
      // Le bouton « retour » d'Android et de l'iPhone ferme le lecteur au lieu de quitter la fiche.
      history.pushState({ videoStories: true }, '');
      this.pushedState = true;
      // Variante choisie sur la fiche au moment de l'ouverture, sinon la première disponible.
      const pageVariant = document.querySelector('form[action*="/cart/add"] [name="id"]');
      const pick = (id) => this.config.variants.find((v) => String(v.id) === String(id) && v.available);
      const wanted = (pageVariant && pick(pageVariant.value)) || pick(this.config.selected) || this.config.variants.find((v) => v.available) || this.config.variants[0];
      this.select.value = String(wanted.id);
      this.syncVariant();
      this.load(index);
      track('video_story_open', this.trackData());
      this.closeBtn.focus({ preventScroll: true });
    }

    load(index) {
      // Jetons : une réponse tardive de la vidéo précédente (hls.js, play()) ne doit plus agir.
      this.loadSeq = (this.loadSeq || 0) + 1;
      this.playToken = (this.playToken || 0) + 1;
      this.current = index;
      const s = this.stories[index];
      this.loading = true;
      this.destroyHls();
      this.video.poster = s.poster;
      this.video.muted = !this.soundOn;
      this.labelEl.textContent = s.label;
      this.bubbles[index].classList.add('is-seen');
      this.paintProgress();
      const pick = s.pick || (s.pick = pickSource(s.sources));
      if (pick.engine !== 'hls') {
        this.video.src = pick.url;
        this.play();
        return;
      }
      this.video.removeAttribute('src');
      this.video.load(); // arrête le téléchargement de la vidéo précédente
      const seq = this.loadSeq;
      loadHls(this.hlsSrc).then((Hls) => {
        if (!this.isOpen || seq !== this.loadSeq) return; // une autre vidéo a été demandée entre-temps
        if (!Hls.isSupported()) throw new Error('hls.js');
        this.destroyHls();
        // Tampon réellement plafonné à 12 s (maxBufferLength seul n'est qu'un plancher dans hls.js) :
        // en stories, ce qui est préchargé au-delà est perdu quand on passe à la vidéo suivante.
        const hls = new Hls({
          capLevelToPlayerSize: true,
          maxBufferLength: 10,
          maxMaxBufferLength: 12,
          abrEwmaDefaultEstimate: startBandwidth(),
        });
        this.hls = hls;
        hls.on(Hls.Events.ERROR, (event, data) => { if (data.fatal && this.hls === hls) this.useFallback(index); });
        hls.on(Hls.Events.MANIFEST_PARSED, () => { if (this.hls === hls) this.play(); });
        hls.loadSource(pick.url);
        hls.attachMedia(this.video);
      }).catch(() => { if (seq === this.loadSeq) this.useFallback(index); });
    }

    // Flux haute définition illisible : on repasse sur le MP4 Shopify de la même vidéo.
    useFallback(index) {
      if (!this.isOpen || this.current !== index) return;
      const s = this.stories[index];
      const alt = fallbackSource(s.sources, s.pick && s.pick.url);
      if (alt) {
        s.pick = { url: alt, engine: 'native' };
        this.load(index);
      } else {
        this.destroyHls();
        this.setPlayButton(true);
      }
    }

    destroyHls() {
      if (!this.hls) return;
      const bw = this.hls.bandwidthEstimate; // débit mesuré, réutilisé au démarrage de la vidéo suivante
      if (bw > 0 && isFinite(bw)) lastBw = Math.min(bw, BW_START);
      this.hls.destroy();
      this.hls = null;
    }

    play() {
      // Seule la dernière demande de lecture compte (un changement de vidéo annule la précédente).
      const token = (this.playToken = (this.playToken || 0) + 1);
      const p = this.video.play();
      if (!p) { this.loading = false; return; }
      p.then(() => {
        if (token === this.playToken) this.loading = false;
      }).catch((err) => {
        if (token !== this.playToken) return;
        // Son refusé par le navigateur (certains navigateurs intégrés d'applications) : on relance en muet,
        // le bouton affiche « son coupé » et un toucher le réactive.
        if (err && err.name === 'NotAllowedError' && !this.video.muted && this.isOpen) {
          this.soundOn = false;
          this.video.muted = true;
          this.paintSound();
          this.play();
          return;
        }
        // Lecture refusée même en muet (mode économie d'énergie sur iPhone, économiseur de données) : bouton lecture.
        this.loading = false;
        if (this.isOpen) this.setPlayButton(true);
      });
    }

    setPlayButton(visible) {
      if (!visible && document.activeElement === this.playBtn) this.closeBtn.focus({ preventScroll: true });
      this.playBtn.hidden = !visible;
    }

    paintSound() {
      const t = this.config.i18n;
      this.soundBtn.classList.toggle('is-muted', !this.soundOn);
      this.soundBtn.setAttribute('aria-label', this.soundOn ? t.mute : t.unmute);
    }

    next() {
      if (this.current < this.stories.length - 1) this.load(this.current + 1);
      else this.close();
    }

    paintProgress() {
      const v = this.video;
      this.segments.forEach((seg, i) => {
        const ratio = i < this.current ? 1 : i > this.current ? 0 : (v.duration ? v.currentTime / v.duration : 0);
        seg.style.transform = `scaleX(${ratio})`;
      });
    }

    syncVariant() {
      const t = this.config.i18n;
      const v = this.config.variants.find((x) => String(x.id) === this.select.value) || this.config.variants[0];
      this.priceEl.textContent = v.price;
      this.addBtn.textContent = v.available ? t.add : t.soldOut;
      this.addBtn.disabled = !v.available;
    }

    async addToCart() {
      if (this.adding || this.addBtn.disabled) return;
      this.adding = true;
      const t = this.config.i18n;
      const id = Number(this.select.value);
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const refresh = () => document.dispatchEvent(new CustomEvent('cart:refresh'));
      const fail = (message) => {
        this.showToast(message || t.error);
        refresh();
        this.adding = false;
        this.addBtn.removeAttribute('aria-busy');
      };
      this.addBtn.setAttribute('aria-busy', 'true');

      let res;
      let json = null;
      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sections.join(','), sections_url: `${root}variants/${id}` })
        });
        json = await res.json().catch(() => null);
      } catch (e) {
        fail(); // réseau : jamais de message technique brut
        return;
      }
      if (!res.ok) {
        // 422 : Shopify renvoie un message déjà traduit (stock insuffisant, etc.).
        fail(res.status === 422 && json && typeof json.description === 'string' ? json.description : null);
        return;
      }

      // Ajout confirmé : plus de message d'échec à partir d'ici.
      track('video_story_add_to_cart', Object.assign(this.trackData(), { variant_id: id }));
      this.addBtn.removeAttribute('aria-busy');
      this.close();
      try {
        const cart = await (await fetch(`${root}cart.js`)).json();
        cart.sections = json && json.sections;
        if (cart.sections) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
        } else {
          refresh();
        }
      } catch (e) {
        refresh();
      }
    }

    showToast(text) {
      clearTimeout(this.toastTimer);
      clearTimeout(this.toastSetTimer);
      this.toast.textContent = '';
      // Vider puis remplir : les lecteurs d'écran annoncent le changement.
      this.toastSetTimer = setTimeout(() => { this.toast.textContent = text; }, 50);
      this.toastTimer = setTimeout(() => { this.toast.textContent = ''; }, 3000);
    }

    trapFocus(e) {
      const items = [...this.player.querySelectorAll('button:not([hidden]):not([tabindex="-1"]):not(:disabled), select:not(:disabled)')]
        .filter((x) => !x.closest('[hidden]'));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!items.includes(document.activeElement)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    close() {
      if (!this.isOpen) return;
      if (this.pushedState) {
        this.pushedState = false;
        history.back(); // déclenche popstate, qui appelle teardown()
        setTimeout(() => { if (this.isOpen) this.teardown(); }, 300);
      } else {
        this.teardown();
      }
    }

    teardown() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.pushedState = false;
      document.removeEventListener('keydown', this.onKey);
      clearTimeout(this.toastTimer);
      clearTimeout(this.toastSetTimer);
      this.toast.textContent = '';
      this.destroyHls();
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      this.playBtn.hidden = true;
      this.player.hidden = true;
      document.documentElement.classList.remove('vs-lock');
      if (this.trigger && this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
    }
  }

  window.customElements.define('video-stories', VideoStories);
})();
